import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
} from 'amazon-cognito-identity-js'

/**
 * Signing in without ever sending the password.
 *
 * The browser and Cognito run an exchange called SRP between themselves. The
 * browser proves it knows the password by doing arithmetic with it, and sends
 * only the result. The password is not transmitted in any form: not in plain
 * text, not hashed, not encrypted. There is nothing on the wire to intercept,
 * nothing passing through our own code, and nothing for us to store or
 * accidentally write to a log.
 *
 * The pool and client ids below are public by design. They identify which
 * directory to talk to and nothing more; they grant no access on their own.
 */

const POOL_ID = import.meta.env.VITE_USER_POOL_ID as string | undefined
const CLIENT_ID = import.meta.env.VITE_USER_POOL_CLIENT_ID as string | undefined

export interface Tokens {
  accessToken: string
  refreshToken: string
}

/**
 * What a sign in produced: either a session, or a demand for a new password.
 *
 * Somebody given a temporary password gets the second. Cognito will not issue
 * a token until they have chosen their own, which is the whole point of a
 * temporary password: the one written in a message stops working the moment it
 * has been used.
 */
export type SignInResult =
  | { kind: 'signedIn'; tokens: Tokens }
  | { kind: 'newPasswordNeeded'; challenge: Challenge }

/**
 * The half finished sign in, held so the next screen can finish it.
 *
 * It lives in memory for the length of one visit and is never written down.
 * Reloading the page loses it, and the person signs in with their temporary
 * password again, which still works because they have not replaced it yet.
 *
 * It carries the way to finish rather than the pieces to finish with, so the
 * screen that collects the new password knows nothing about Cognito, and the
 * offline stand-in can hand back the same thing and behave the same way.
 */
export interface Challenge {
  finish: (password: string) => Promise<Tokens>
}

export class SignInError extends Error {}

/** The one message every failure gives, whatever actually went wrong. */
const REFUSED = 'That email address and password do not match.'

/**
 * One Cognito directory serves every company, so a username carries the
 * company in front of the email. The person only ever types their email; the
 * app looks the company up first and adds the prefix.
 */
export async function signInWithSrp(
  tenant: string,
  email: string,
  password: string,
): Promise<SignInResult> {
  if (!POOL_ID || !CLIENT_ID) {
    throw new SignInError(
      'Sign in is not configured for this build. The user pool details are missing.',
    )
  }

  const pool = new CognitoUserPool({ UserPoolId: POOL_ID, ClientId: CLIENT_ID })
  const user = new CognitoUser({ Username: `${tenant}#${email.toLowerCase()}`, Pool: pool })
  const details = new AuthenticationDetails({
    Username: `${tenant}#${email.toLowerCase()}`,
    Password: password,
  })

  return new Promise<SignInResult>((resolve, reject) => {
    user.authenticateUser(details, {
      onSuccess: (session) => {
        resolve({
          kind: 'signedIn',
          tokens: {
            accessToken: session.getAccessToken().getJwtToken(),
            refreshToken: session.getRefreshToken().getToken(),
          },
        })
      },
      // Every failure reads the same, so the form cannot be used to work out
      // which email addresses exist.
      onFailure: () => reject(new SignInError(REFUSED)),
      /**
       * The temporary password was right, and it is spent.
       *
       * Not an error. The person typed the correct thing and is one screen
       * away from being in, so this resolves rather than rejects and the app
       * shows them the screen that finishes the job.
       */
      newPasswordRequired: (attributes: Record<string, unknown>) => {
        // Cognito hands back read only values alongside the real ones and
        // refuses the reply if they are sent back, which reads as a wrong
        // password to somebody who typed nothing wrong.
        const required = { ...attributes }
        delete required.email_verified
        delete required.phone_number_verified
        delete required.email
        resolve({
          kind: 'newPasswordNeeded',
          challenge: { finish: (chosen) => setNewPassword(user, required as Record<string, string>, chosen) },
        })
      },
      mfaRequired: () =>
        reject(new SignInError('This account needs a verification code, which is not built yet.')),
    })
  })
}

/**
 * Finishes a sign in by setting the password the person chose.
 *
 * Cognito checks the new password against the pool's rules, so a refusal here
 * is a real reason worth showing rather than the deliberately vague one the
 * sign in form gives.
 */
function setNewPassword(
  user: CognitoUser,
  required: Record<string, string>,
  password: string,
): Promise<Tokens> {
  return new Promise<Tokens>((resolve, reject) => {
    user.completeNewPasswordChallenge(password, required, {
      onSuccess: (session) => {
        resolve({
          accessToken: session.getAccessToken().getJwtToken(),
          refreshToken: session.getRefreshToken().getToken(),
        })
      },
      onFailure: (err: Error) => reject(new SignInError(readable(err))),
    })
  })
}

/** Turns Cognito's wording into something worth reading. */
function readable(err: Error): string {
  const raw = err?.message ?? ''
  if (/password/i.test(raw) && /policy|long|uppercase|lowercase|numeric|symbol/i.test(raw)) {
    return 'That password is not strong enough. Use at least twelve characters with a capital, a small letter and a number.'
  }
  if (/expired/i.test(raw)) {
    return 'That temporary password has expired. Ask for a new one.'
  }
  return raw || 'That password could not be set. Try again.'
}
